import { useCreateAccountsSettingsDictionary } from '@/shared/api';
import type { AllowedAccountsQuantity } from '@/shared/types';
import { useOpenAccountAvailabilityStore } from './useOpenAccountAvailabilityStore';
import { useEffect } from 'react';
import {
  getRegularAccountsAssets,
  type AccountsMarginRestsBaseResponseDto,
  type AccountsRestsBaseResponseDto,
  type AccountsRestsByType,
} from '@/entities/account';
import {
  usePendingAccountsStore,
  type PendingAccount,
} from './pendingAccountsStore';

const atLeastOneAssetWithoutAccounts = (
  allowedAssets: Array<string>,
  accountList: Array<AccountsRestsBaseResponseDto>,
  pendingAccounts: PendingAccount[]
) => {
  const existingAssets = getRegularAccountsAssets(accountList);
  const pendingAssets = pendingAccounts
    .filter(account => account.accountType === 'invest')
    .map(account => account.assetCode);

  const takenAssets = [
    ...existingAssets.map(asset => asset.code),
    ...pendingAssets,
  ];

  return allowedAssets.some(
    allowedAsset => !takenAssets.includes(allowedAsset)
  );
};

const isAllowedToCreateRegularAccount = (
  accountAllowedQuantity: AllowedAccountsQuantity,
  accountList: Array<AccountsRestsBaseResponseDto>,
  allowedAssets: Array<string>,
  pendingAccounts: PendingAccount[]
) => {
  switch (accountAllowedQuantity) {
    case 'one_only':
      return (
        accountList.length === 0 &&
        !pendingAccounts.some(account => account.accountType === 'spot')
      );
    case 'one_account_each_asset':
      return !allowedAssets
        ? false
        : atLeastOneAssetWithoutAccounts(
            allowedAssets,
            accountList,
            pendingAccounts
          );
    case 'without_restrictions':
      return true;
  }
};

const isAllowedToCreateMarginAccount = (
  accountAllowedQuantity: AllowedAccountsQuantity,
  accountList: AccountsMarginRestsBaseResponseDto | null,
  allowedAssets: Array<string>,
  pendingAccountType?: 'invest' | 'margin' | 'earn',
  pendingAccounts?: PendingAccount[]
) => {
  if (!accountList) {
    return true;
  }

  switch (accountAllowedQuantity) {
    case 'one_only':
      return (
        accountList.accounts.length === 0 &&
        !pendingAccounts?.some(
          account => account.accountType === pendingAccountType
        )
      );
    case 'one_account_each_asset': {
      if (!allowedAssets) return false;
      const takenAssets = [
        ...accountList.accounts
          .map(account => account.balanceAsset?.code)
          .filter((code): code is string => !!code),
        ...(pendingAccounts || [])
          .filter(account => account.accountType === pendingAccountType)
          .map(account => account.assetCode || ''),
      ];
      return allowedAssets.some(
        allowedAsset => !takenAssets.includes(allowedAsset)
      );
    }
    case 'without_restrictions':
      return true;
  }
};

const useOpenAccountAvailability = (
  accountsRestsByType: AccountsRestsByType | null | undefined
) => {
  const {
    setAccountsCreateAvailability,
    setAccountsCreateActive,
    openSpotIsAvailable,
    openInvestIsAvailable,
    openMarginIsAvailable,
    openEarnIsAvailable,
    spotCreateIsActive,
    investCreateIsActive,
    marginCreateIsActive,
    earnCreateIsActive,
  } = useOpenAccountAvailabilityStore();

  const { pendingAccounts } = usePendingAccountsStore();

  const { createAccountsSettingsData, createAccountsSettingsLoading } =
    useCreateAccountsSettingsDictionary();

  const { spot, invest, leverage, earn } =
    createAccountsSettingsData?.value || {};

  useEffect(() => {
    if (!spot || !invest || !leverage || !earn || !accountsRestsByType) {
      setAccountsCreateAvailability(false, false, false, false);
      setAccountsCreateActive(false, false, false, false);
    } else {
      setAccountsCreateActive(
        spot.isCreateActive,
        invest.isCreateActive,
        leverage.isCreateActive,
        earn.isCreateActive
      );
      setAccountsCreateAvailability(
        isAllowedToCreateRegularAccount(
          spot?.allowedAccountsQuantity,
          accountsRestsByType.spot,
          spot?.assets,
          pendingAccounts
        ),
        isAllowedToCreateMarginAccount(
          invest?.allowedAccountsQuantity,
          accountsRestsByType.invest,
          invest?.assets,
          'invest',
          pendingAccounts
        ),
        isAllowedToCreateMarginAccount(
          leverage?.allowedAccountsQuantity,
          accountsRestsByType.leverage,
          leverage?.assets
        ),
        isAllowedToCreateMarginAccount(
          earn?.allowedAccountsQuantity,
          accountsRestsByType.earn,
          earn?.assets,
          'earn',
          pendingAccounts
        )
      );
    }
  }, [spot, invest, leverage, earn, pendingAccounts, accountsRestsByType]);

  return {
    createAccountsSettingsData,
    createAccountsSettingsLoading,
    openSpotIsAvailable,
    openInvestIsAvailable,
    openMarginIsAvailable,
    openEarnIsAvailable,
    spotCreateIsActive,
    investCreateIsActive,
    marginCreateIsActive,
    earnCreateIsActive,
    pendingAccounts,
  };
};

export { useOpenAccountAvailability };
